type FieldSection = 'shipper' | 'consignee' | 'orderDetails';
type FieldType = 'text' | 'phone' | 'email' | 'textarea';

export interface IFieldRegistryEntry {
    /** Dot-path to read value from Redux formData (customer portal paths) */
    fieldPath: string;
    label: string;
    section: FieldSection;
    fieldType: FieldType;
    placeholder?: string;
    /** Defaults to required. Only BOL Instructions is optional (client decision) — it is the one
     *  field with no carry-over source, and the carrier does not need it to produce a BOL. */
    isRequired?: boolean;
}

const BOL_FIELD_REGISTRY: Record<string, IFieldRegistryEntry> = {
    shipperContactName: {
        fieldPath: 'shipperDetails.shipperContactName',
        label: 'Shipper Contact Name',
        section: 'shipper',
        fieldType: 'text',
        placeholder: 'Enter contact name',
    },
    shipperContactNumber: {
        fieldPath: 'shipperDetails.shipperContactPhone',
        label: 'Shipper Phone Number',
        section: 'shipper',
        fieldType: 'phone',
        placeholder: 'Enter phone number',
    },
    shipperEmail: {
        fieldPath: 'shipperDetails.shipperContactEmail',
        label: 'Shipper Email',
        section: 'shipper',
        fieldType: 'email',
        placeholder: 'Enter email',
    },
    consigneeContactName: {
        fieldPath: 'consigneeDetails.consigneeContactName',
        label: 'Consignee Contact Name',
        section: 'consignee',
        fieldType: 'text',
        placeholder: 'Enter contact name',
    },
    consigneeContactNumber: {
        fieldPath: 'consigneeDetails.consigneeContactPhone',
        label: 'Consignee Phone Number',
        section: 'consignee',
        fieldType: 'phone',
        placeholder: 'Enter phone number',
    },
    consigneeEmail: {
        fieldPath: 'consigneeDetails.consigneeContactEmail',
        label: 'Consignee Email',
        section: 'consignee',
        fieldType: 'email',
        placeholder: 'Enter email',
    },
    specialNotes: {
        fieldPath: '',
        label: 'BOL Instructions',
        section: 'orderDetails',
        fieldType: 'textarea',
        placeholder: 'Enter special notes',
        isRequired: false,
    },
};

const ALLOWED_BOL_FIELDS = new Set(['shipperContactName', 'shipperContactNumber', 'shipperEmail', 'consigneeContactName', 'consigneeContactNumber', 'consigneeEmail']);

/** Get resolved fields for the BOL step from carrier bol config */
export const getBolVisibleFields = (
    commonCarrierRequiredFields: { slug: string; carrier: string[]; bol: string[] }[],
    carrierSlug: string | null | undefined
): (IFieldRegistryEntry & { key: string })[] => {
    const specialNotesField = { key: 'specialNotes', ...BOL_FIELD_REGISTRY.specialNotes };

    if (!carrierSlug) return [specialNotesField];

    const config = commonCarrierRequiredFields.find((c) => c.slug === carrierSlug);
    const bolFieldNames = config?.carrier ?? [];
    const contactFields = bolFieldNames.filter((name) => ALLOWED_BOL_FIELDS.has(name) && BOL_FIELD_REGISTRY[name]).map((name) => ({ key: name, ...BOL_FIELD_REGISTRY[name] }));

    return [...contactFields, specialNotesField];
};

/**
 * Effective value of one BOL field before the step has been opened: whatever was saved on the step,
 * otherwise the matching contact detail carried over from the Shipper / Consignee steps.
 *
 * Lives here, not in the step component, so the stepper's pre-booking gate judges exactly the values
 * the step itself would display. Two copies of this rule would let the gate pass a field the step
 * shows as empty, or bounce the user to a step that looks complete.
 *
 * Note: the `isData` probe reads shipperContactName on BOTH sides — consignee included. Preserved
 * verbatim from the step's own logic; changing it would alter which value wins for consignee fields.
 */
export const resolveBolFieldValue = (fieldKey: string, formData: any): string => {
    const savedBolData: Record<string, string> = formData?.bolRequirementsData ?? {};
    const bolUserEditedFields: string[] = formData?.bolUserEditedFields ?? [];

    const isData = fieldKey.startsWith('shipper') ? formData?.shipperDetails?.shipperContactName : formData?.consigneeDetails?.shipperContactName;
    if (fieldKey in savedBolData && !!savedBolData[fieldKey] && isData) return savedBolData[fieldKey] ?? '';

    const isFieldData = bolUserEditedFields.includes(fieldKey) && !isData ? savedBolData[fieldKey] : '';
    if (fieldKey === 'shipperContactName') return (isFieldData || formData?.shipperDetails?.shipperContactName) ?? '';
    if (fieldKey === 'shipperContactNumber') return (isFieldData || (formData?.shipperDetails?.shipperContactPhone ?? formData?.shipperDetails?.shipperContactNumber)) ?? '';
    if (fieldKey === 'shipperEmail') return (isFieldData || formData?.shipperDetails?.shipperContactEmail) ?? '';
    if (fieldKey === 'consigneeContactName') return (isFieldData || formData?.consigneeDetails?.consigneeContactName) ?? '';
    if (fieldKey === 'consigneeContactNumber') return (isFieldData || (formData?.consigneeDetails?.consigneeContactPhone ?? formData?.consigneeDetails?.consigneeContactNumber)) ?? '';
    if (fieldKey === 'consigneeEmail') return (isFieldData || formData?.consigneeDetails?.consigneeContactEmail) ?? '';
    if (fieldKey === 'specialNotes') return savedBolData[fieldKey] ?? '';

    return '';
};

/** True when a BOL field the user must fill is still blank — the pre-booking gate's test.
 *  Optional fields (isRequired === false, i.e. BOL Instructions) never fail the gate. */
export const hasEmptyRequiredBolField = (commonCarrierRequiredFields: { slug: string; carrier: string[]; bol: string[] }[], carrierSlug: string | null | undefined, formData: any): boolean =>
    getBolVisibleFields(commonCarrierRequiredFields, carrierSlug)
        .filter((field) => field.isRequired !== false)
        .some((field) => !String(resolveBolFieldValue(field.key, formData) ?? '').trim());

export const groupFieldsBySection = (fields: (IFieldRegistryEntry & { key: string })[]): Partial<Record<FieldSection, (IFieldRegistryEntry & { key: string })[]>> =>
    fields.reduce((acc, field) => {
        if (!acc[field.section]) acc[field.section] = [];
        acc[field.section]!.push(field);

        return acc;
    }, {} as Partial<Record<FieldSection, (IFieldRegistryEntry & { key: string })[]>>);
